import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { timeAgo } from '@/lib/timeAgo';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import type { ScanDocError } from '@/types/api';

interface ScanFooterProps {
  data: {
    scannedAt: string;
    modelUsed: string;
    totalFiles: number;
    errors?: ScanDocError[];
    stoppedEarly?: boolean;
    staleFiles?: number;
  };
}

export function ScanFooter({ data }: ScanFooterProps) {
  const tz = useDisplayTimezone();
  const [showSkipped, setShowSkipped] = useState(false);
  const errorCount = data.errors?.length ?? 0;

  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-400 flex items-center gap-2">
        <span title={formatInstant(data.scannedAt, tz)}>Scanned {timeAgo(data.scannedAt, tz)}</span>
        <span>&middot;</span>
        <span>{data.modelUsed}</span>
        <span>&middot;</span>
        <span>{data.totalFiles} file{data.totalFiles !== 1 ? 's' : ''}</span>
        {(data.staleFiles ?? 0) > 0 && (
          <>
            <span>&middot;</span>
            <span className="text-orange-500 font-medium">
              {data.staleFiles} file{data.staleFiles !== 1 ? 's' : ''} changed since scan
            </span>
          </>
        )}
        {errorCount > 0 && (
          <>
            <span>&middot;</span>
            <button
              onClick={() => setShowSkipped(!showSkipped)}
              className="inline-flex items-center gap-0.5 text-slate-400 hover:text-slate-600"
            >
              {showSkipped
                ? <ChevronDown className="w-3 h-3" />
                : <ChevronRight className="w-3 h-3" />}
              {errorCount} skipped
            </button>
          </>
        )}
      </div>
      {showSkipped && data.errors && (
        <div className="text-xs text-slate-400 pl-1 space-y-0.5">
          {data.errors.map((err, i) => (
            <p key={i}>
              {err.document}{err.message ? `: ${err.message}` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
